interface Message {
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

interface ChatbotState {
  isOpen: boolean;
  messages: Message[];
}

export class Chatbot {
  private state: ChatbotState = {
    isOpen: false,
    messages: [
      {
        role: 'assistant',
        content: 'Bonjour ! Je suis l\'assistant virtuel d\'Arthur Miconnet. Je peux repondre a vos questions sur son parcours, ses competences, ses projets ou comment le contacter. Comment puis-je vous aider ?',
        timestamp: new Date()
      }
    ]
  };

  private container: HTMLElement | null = null;
  private errorElement: HTMLElement | null = null;

  constructor() {
    this.render();
    this.attachEventListeners();
  }

  private render(): void {
    const chatbotHTML = `
      <!-- Chatbot Toggle Button -->
      <button
        id="chatbot-button"
        class="fixed bottom-6 right-6 w-14 h-14 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 z-50 flex items-center justify-center"
        style="background-color: var(--color-primary);"
        aria-label="Ouvrir le chatbot"
        aria-expanded="false"
        aria-controls="chatbot-window">
        <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"></path>
        </svg>
      </button>

      <!-- Chatbot Window -->
      <div
        id="chatbot-window"
        role="dialog"
        aria-label="Assistant virtuel du portfolio"
        aria-modal="false"
        class="fixed bottom-24 right-4 sm:right-6 w-[calc(100vw-2rem)] sm:w-full max-w-md h-[min(500px,calc(100vh-8rem))] z-50 flex flex-col overflow-hidden transition-all duration-300 origin-bottom-right rounded-2xl"
        style="display: none; opacity: 0; transform: scale(0.9); background-color: var(--bg-elevated); border: 1px solid var(--border-color); box-shadow: 0 20px 60px rgba(0,0,0,0.12);">

        <!-- Header -->
        <div class="flex items-center justify-between p-4 border-b" style="border-color: var(--border-color);">
          <div class="flex items-center gap-3">
            <div class="w-9 h-9 rounded-xl flex items-center justify-center font-display font-bold text-white text-xs" style="background-color: var(--color-primary);">
              AM
            </div>
            <div>
              <h2 class="font-display font-semibold text-body-sm" style="color: var(--text-primary)">Assistant Portfolio</h2>
              <p class="font-body text-caption" style="color: var(--text-tertiary)">En ligne</p>
            </div>
          </div>
          <button
            id="chatbot-close"
            class="w-9 h-9 rounded-lg flex items-center justify-center transition-colors"
            style="color: var(--text-tertiary);"
            aria-label="Fermer le chatbot">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>

        <!-- Messages -->
        <div
          id="chatbot-messages"
          class="flex-1 overflow-y-auto p-4 space-y-3"
          style="background-color: var(--bg-subtle);"
          role="log"
          aria-live="polite"
          aria-label="Historique des messages">
        </div>

        <!-- Input -->
        <div class="p-4 border-t" style="border-color: var(--border-color); background-color: var(--bg-elevated);">
          <div class="flex gap-2">
            <label for="chatbot-input" class="sr-only">Votre message</label>
            <input
              type="text"
              id="chatbot-input"
              placeholder="Posez votre question..."
              autocomplete="off"
              class="flex-1 px-4 py-2.5 rounded-xl border font-body text-body-sm transition-colors"
              style="border-color: var(--border-color); background-color: var(--bg-subtle); color: var(--text-primary);"
              aria-describedby="chatbot-error"
            />
            <button
              id="chatbot-send"
              class="w-11 h-11 rounded-xl flex items-center justify-center transition-all text-white flex-shrink-0"
              style="background-color: var(--color-primary);"
              aria-label="Envoyer le message">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"></path>
              </svg>
            </button>
          </div>

          <!-- Quick Questions -->
          <div class="mt-3 flex flex-wrap gap-2" id="quick-questions" role="group" aria-label="Questions rapides">
            <button class="quick-question font-body text-caption px-3 py-1.5 rounded-full transition-colors" style="background-color: rgba(67, 56, 202, 0.06); color: var(--color-primary);">
              Quel est son parcours ?
            </button>
            <button class="quick-question font-body text-caption px-3 py-1.5 rounded-full transition-colors" style="background-color: rgba(67, 56, 202, 0.06); color: var(--color-primary);">
              Quelles competences ?
            </button>
            <button class="quick-question font-body text-caption px-3 py-1.5 rounded-full transition-colors" style="background-color: rgba(67, 56, 202, 0.06); color: var(--color-primary);">
              Comment le contacter ?
            </button>
          </div>

          <p id="chatbot-error" class="mt-2 font-body text-body-sm" style="color: #EF4444; display: none;" role="alert" aria-live="assertive"></p>
        </div>
      </div>
    `;

    const div = document.createElement('div');
    div.id = 'chatbot-container';
    div.innerHTML = chatbotHTML;
    document.body.appendChild(div);
    this.container = div;
    this.errorElement = document.getElementById('chatbot-error');

    this.renderMessages();
  }

  private sanitizeHTML(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  private renderMessages(): void {
    const messagesContainer = document.getElementById('chatbot-messages');
    if (!messagesContainer) return;

    messagesContainer.innerHTML = this.state.messages.map(msg => {
      const isUser = msg.role === 'user';
      return `
        <div class="flex ${isUser ? 'justify-end' : 'justify-start'}">
          <div class="max-w-[80%] rounded-2xl px-4 py-3 ${isUser ? '' : ''}" style="${isUser
            ? 'background-color: var(--color-primary); color: white;'
            : 'background-color: var(--bg-elevated); color: var(--text-primary); border: 1px solid var(--border-color);'
          }">
            <p class="font-body text-body-sm whitespace-pre-wrap">${this.sanitizeHTML(msg.content)}</p>
          </div>
        </div>
      `;
    }).join('');

    messagesContainer.scrollTop = messagesContainer.scrollHeight;
  }

  private attachEventListeners(): void {
    const button = document.getElementById('chatbot-button');
    const closeBtn = document.getElementById('chatbot-close');

    button?.addEventListener('click', () => this.toggleChatbot());
    closeBtn?.addEventListener('click', () => this.toggleChatbot());

    const sendBtn = document.getElementById('chatbot-send');
    const input = document.getElementById('chatbot-input') as HTMLInputElement;

    sendBtn?.addEventListener('click', () => this.sendMessage());
    input?.addEventListener('keypress', (e) => {
      if (e.key === 'Enter') this.sendMessage();
    });

    document.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      if (target.classList.contains('quick-question')) {
        const question = target.textContent?.trim();
        if (question) {
          const inp = document.getElementById('chatbot-input') as HTMLInputElement;
          if (inp) {
            inp.value = question;
            this.sendMessage();
          }
        }
      }
    });

    // Close on Escape
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.state.isOpen) {
        this.toggleChatbot();
      }
    });
  }

  private toggleChatbot(): void {
    this.state.isOpen = !this.state.isOpen;
    const chatWindow = document.getElementById('chatbot-window');
    const button = document.getElementById('chatbot-button');

    if (!chatWindow || !button) return;

    button.setAttribute('aria-expanded', String(this.state.isOpen));

    if (this.state.isOpen) {
      chatWindow.style.display = 'flex';
      button.setAttribute('aria-label', 'Fermer le chatbot');
      setTimeout(() => {
        chatWindow.style.opacity = '1';
        chatWindow.style.transform = 'scale(1)';
        // Focus the input when opened
        const input = document.getElementById('chatbot-input');
        input?.focus();
      }, 10);
    } else {
      chatWindow.style.opacity = '0';
      chatWindow.style.transform = 'scale(0.9)';
      button.setAttribute('aria-label', 'Ouvrir le chatbot');
      // Return focus to the toggle button
      button.focus();
      setTimeout(() => {
        chatWindow.style.display = 'none';
      }, 300);
    }
  }

  private sendMessage(): void {
    const input = document.getElementById('chatbot-input') as HTMLInputElement;
    if (!input) return;

    const userMessage = input.value.trim();
    if (!userMessage) {
      this.showError('Merci de saisir un message.');
      return;
    }

    if (userMessage.length < 2) {
      this.showError('Merci d\'utiliser au moins 2 caracteres.');
      return;
    }

    if (userMessage.length > 1000) {
      this.showError('Le message est trop long (maximum 1000 caracteres).');
      return;
    }

    this.showError('');
    input.value = '';

    this.state.messages.push({
      role: 'user',
      content: userMessage,
      timestamp: new Date()
    });

    this.renderMessages();

    setTimeout(() => {
      const response = this.getResponse(userMessage);
      this.state.messages.push({
        role: 'assistant',
        content: response,
        timestamp: new Date()
      });
      this.renderMessages();
    }, 500);
  }

  private showError(message: string): void {
    if (!this.errorElement) return;
    this.errorElement.textContent = message;
    this.errorElement.style.display = message ? 'block' : 'none';
  }

  private getResponse(message: string): string {
    const lowerMessage = message.toLowerCase();

    if (lowerMessage.match(/parcours|formation|étude|diplôme|école/)) {
      return `Arthur a un parcours complet en UX/UI Design :

Formation :
- MBA Expert UX/UI Designer - MyDigitalSchool, Lyon (2020-2023)
- BTS SIO - Formation HTML, CSS, PHP (2017-2020)

Il a 4+ ans d'experience professionnelle dans le design d'interfaces et l'UX/UI. Souhaitez-vous en savoir plus sur son experience professionnelle ?`;
    }

    if (lowerMessage.match(/expérience|travail|emploi|poste|okteo|wanimo/)) {
      return `Arthur a une solide experience professionnelle :

Experiences cles :
- UX/UI Designer chez OKTEO (2022-2025) - Alternance puis CDI
  Conception d'interfaces SaaS, design system, Azure DevOps

- Integrateur Web chez Avenir Multimedia (2021-2022)
  Integration responsive, optimisation performances

- Graphiste chez Wanimo (2020-2021)
  Visuels web/print, refonte mobile

- Developpeur Web chez CREA2F (2018)
  Modules intranet PHP/Bootstrap

Il cumule donc plus de 4 ans d'experience dans le digital !`;
    }

    if (lowerMessage.match(/compétence|skill|savoir|maîtrise|outil|logiciel|technologie/)) {
      return `Arthur possede un large eventail de competences :

Design UX/UI :
- Recherche utilisateur & tests
- Prototypage & maquettage
- Design system & accessibilite
- Figma, Adobe XD, Sketch

Developpement :
- HTML, CSS, PHP, SQL
- Integration responsive
- Azure DevOps

Autres competences :
- Marketing, SEO/SEA
- Adobe Creative Suite
- Methodologie de projet
- Anglais C1

Une competence particuliere vous interesse ?`;
    }

    if (lowerMessage.match(/contact|joindre|email|téléphone|appel|écrire|linkedin/)) {
      return `Vous pouvez contacter Arthur via :

Email : arthur.miconnet1@gmail.com
Telephone : 06 73 20 81 98
Site web : am-dev.eu
LinkedIn : @Arthur Miconnet

Il est disponible pour discuter de vos projets UX/UI !`;
    }

    if (lowerMessage.match(/projet|réalisation|portfolio|travaux|créations/)) {
      return `Arthur a travaille sur divers projets UX/UI, notamment :

- Solutions SaaS chez OKTEO
- Sites e-commerce et vitrines
- Design systems et interfaces
- Refontes mobiles

Pour voir ses projets en detail, je vous invite a consulter la section "Projets" du portfolio. Voulez-vous connaitre ses competences ou son experience ?`;
    }

    if (lowerMessage.match(/qualité|personnalité|caractère|atout|point fort/)) {
      return `Arthur se distingue par ses qualites professionnelles :

Curieux - Toujours en veille sur les nouvelles tendances UX/UI
Rigoureux - Methodique dans ses approches de conception
Creatif - Capacite a proposer des solutions innovantes
Dynamique - Proactif et engage dans ses projets

Il transforme la complexite metier en parcours simples grace a la recherche ciblee, le prototypage rapide et la mesure post-lancement.`;
    }

    if (lowerMessage.match(/disponible|disponibilité|recrut|embauche|poste|cdi|freelance/)) {
      return `Pour connaitre la disponibilite d'Arthur et discuter d'opportunites, je vous invite a le contacter directement :

Email : arthur.miconnet1@gmail.com
Telephone : 06 73 20 81 98

Il sera ravi d'echanger avec vous sur vos besoins !`;
    }

    if (lowerMessage.match(/âge|ans|vieux|jeune|né|naissance/)) {
      return `Arthur a 26 ans et cumule deja 4+ ans d'experience dans le design UX/UI. Il possede egalement le permis B et parle anglais au niveau C1.`;
    }

    if (lowerMessage.match(/où|ville|région|lyon|localisation|habite/)) {
      return `Arthur a travaille dans la region Auvergne-Rhone-Alpes, notamment a Lyon et Ceyzeriat. Il possede le permis B, ce qui lui offre une bonne mobilite.

Pour plus d'informations sur sa disponibilite geographique, contactez-le a : arthur.miconnet1@gmail.com`;
    }

    if (lowerMessage.match(/\b(bonjour|salut|hello|hi|hey|coucou)\b/)) {
      return `Bonjour ! Comment puis-je vous aider a en savoir plus sur Arthur Miconnet ? Je peux vous renseigner sur son parcours, ses competences, ses projets ou comment le contacter.`;
    }

    if (lowerMessage.match(/merci|thanks|thx/)) {
      return `Je vous en prie ! N'hesitez pas si vous avez d'autres questions sur Arthur. Bonne visite du portfolio !`;
    }

    return `Je peux vous renseigner sur :

- Son parcours et sa formation
- Son experience professionnelle
- Ses competences et outils
- Comment le contacter
- Ses qualites et sa personnalite
- Ses projets

Quelle information recherchez-vous ?`;
  }
}
